import {
  DataGrid,
  EmptyState,
  Icon,
  Spinner,
  theme,
  IconDictionary,
  TabSegmented,
  TabSegmentedGroup,
  Avatar,
  AvatarSize,
  TableCell,
  Chip,
} from '@ketch-com/deck'
import {
  EventsBySystemDetail,
  Classification,
  ClassificationType,
} from '@ketch-com/windlass/dist/dataSentry/v1/events/events'
import { useEventsBySystem } from 'api/dataSentry/queries/useDataSentryEventsBySystem'
import { subDays } from 'date-fns'
import format from 'date-fns/format'
import { useDataSentryContext } from 'pages/dataSentry/context/DataSentryContext'
import { useMemo, useRef, useState } from 'react'
import { getSpectrum } from 'pages/dataSentry/data/utils/DataSentryEventsDataGridUtil'
import { useTableFilters } from 'pages/dataSentry/hooks/useTableFilters'
import { SearchTextInput } from 'components/searchTextInput/SearchTextInput'
import { MaybeNull } from 'interfaces'
import { AuthorizationStatus as SystemsAuthorizationStatus } from '@ketch-com/windlass/dist/dataSentry/v1/systems/systems'
import { Box, Typography, Tooltip } from '@mui/material'
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { formatToK } from 'pages/dataSentry/utils/transformPieChartData'
import { getSystemName } from 'pages/dataSentry/utils/getSystemName'
import type { DataSentrySystemsRowItem } from 'pages/dataSentry/systems/systemsTabComponents/utils/DataSentrySystemsDataGridUtil'

export const SystemsTable: React.FC = () => {
  const [systemsAuthorizationStatus, setSystemsAuthorizationStatus] = useState<SystemsAuthorizationStatus>(
    SystemsAuthorizationStatus.AUTHORIZED,
  )

  const { searchQuery, setSearchQuery, paginationModel, setPaginationModel, debouncedSearchQuery } = useTableFilters()
  const { selectedTimeRange, selectedPropertyCode, dataTypesTotalCount } = useDataSentryContext()
  const { data: tableData, isLoading } = useEventsBySystem({
    params: {
      'start-date': selectedTimeRange?.startDate || format(subDays(new Date(), 1), 'yyyy-MM-dd'),
      'end-date': selectedTimeRange?.endDate || format(new Date(), 'yyyy-MM-dd'),
      'property-codes': selectedPropertyCode,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      query: debouncedSearchQuery,
      'authorization-status': systemsAuthorizationStatus,
    },
  })

  const rowCountRef = useRef(tableData.total || 0)
  const rowCount = useMemo(() => {
    if (tableData.total !== undefined) {
      rowCountRef.current = tableData.total
    }
    return rowCountRef.current
  }, [tableData])

  const gridData: MaybeNull<DataSentrySystemsRowItem[]> = useMemo(() => {
    if (!tableData?.dataSystems?.length) return null

    return tableData.dataSystems.map((system: EventsBySystemDetail) => {
      const categories =
        system.classificationsByType
          ?.find(cls => cls.classificationType === ClassificationType.DATA_CATEGORY)
          ?.classifications?.map((c: Classification) => c?.name || '') || []

      const dataTypes =
        system.classificationsByType?.find(cls => cls.classificationType === ClassificationType.DATA_TYPE)
          ?.classifications || []

      const dataTypeCount = dataTypes.length

      let systemCount = system.count || 0
      const collectionEvents = systemCount.toString()

      const spectrum = getSpectrum(dataTypeCount, dataTypesTotalCount || 0)

      return {
        id: system.dataSystem?.code,
        name: system.dataSystem?.name || '',
        code: system.dataSystem?.code,
        logo: system.dataSystem?.logoUrl || '',
        trackers: system.trackers,
        trackerCount: system.trackerCount,
        collectionEvents,
        dataCategories: categories,
        collectionSpectrum: spectrum,
      }
    })
  }, [tableData, dataTypesTotalCount])

  const columns: GridColDef<DataSentrySystemsRowItem>[] = [
    {
      field: 'name',
      headerName: 'System',
      flex: 1.5,
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams<DataSentrySystemsRowItem>) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: (params: GridRenderCellParams<DataSentrySystemsRowItem>) => {
        const logo = params.row.logo
        const hasLogo = !!logo?.trim()

        return (
          <Box display="flex" alignItems="center" gap={1.5} pl={1}>
            {hasLogo ? (
              <Avatar
                src={logo}
                alt={params.value}
                size={AvatarSize['large']}
                borderColor={theme.palette.Black.o8}
                isLogo
              />
            ) : (
              <Avatar
                icon="NoLogo"
                textColor={theme.palette.Text.Secondary}
                borderColor={theme.palette.Black.o8}
                size={AvatarSize['large']}
                isLogo
              />
            )}

            {params.row.name ? (
              <Typography variant="label">{params.row.name}</Typography>
            ) : (
              <Box display="flex" flexDirection="column">
                <Typography component="div" variant="label">
                  Unknown System
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="footnote">{params.row.id}</Typography>
                </Box>
              </Box>
            )}
          </Box>
        )
      },
    },
    {
      field: 'trackers',
      headerName: 'Trackers',
      align: 'right',
      headerAlign: 'center',
      flex: 0.5,
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams<DataSentrySystemsRowItem>) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<DataSentrySystemsRowItem>) => {
        return (
          <Box>
            {
              <Tooltip
                placement="bottom"
                title={
                  row?.trackers?.length ? (
                    <Box display="flex" flexDirection="column" gap={0.5} maxHeight={120} overflow="auto">
                      {row?.trackers?.map((tracker, i) => <Typography key={tracker + i}>{tracker}</Typography>)}
                    </Box>
                  ) : (
                    ''
                  )
                }
              >
                <Box>
                  <Chip size="small" label={row?.trackerCount} />
                </Box>
              </Tooltip>
            }
          </Box>
        )
      },
    },
    {
      align: 'left',
      field: 'usheredBy',
      headerName: 'Ushered By',
      flex: 0.75,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams<DataSentrySystemsRowItem>) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: (params: GridRenderCellParams<DataSentrySystemsRowItem>) => {
        const hasUsheredBy = (params?.row?.usheredBy?.length || 0) > 0
        const usheredBy = params?.row?.usheredBy
        return (
          <TableCell>
            {hasUsheredBy ? (
              <Box display="flex" gap={1} alignItems="center">
                <Tooltip
                  placement="bottom"
                  title={
                    <Box display="flex" flexDirection="column" gap={0.5} maxHeight={120} overflow="auto">
                      {usheredBy?.map(system => <Typography key={system?.code}>{getSystemName(system)}</Typography>)}
                    </Box>
                  }
                >
                  <Box>
                    {usheredBy?.length === 1 ? (
                      <Avatar
                        src={usheredBy?.[0]?.logoUrl || ''}
                        icon={usheredBy?.[0]?.logoUrl ? undefined : IconDictionary.NoLogo}
                        alt={usheredBy?.[0]?.name || ''}
                        textColor={usheredBy?.[0]?.logoUrl ? theme.palette.Black.o48 : theme.palette.Text.Secondary}
                        size={AvatarSize['small']}
                        isLogo
                      />
                    ) : (
                      <Chip label={usheredBy?.length} size="small" />
                    )}
                  </Box>
                </Tooltip>
              </Box>
            ) : (
              'None'
            )}
          </TableCell>
        )
      },
    },

    {
      field: 'collectionEvents',
      headerName: 'Collection Events',
      flex: 0.75,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams<DataSentrySystemsRowItem>) => (
        <Typography
          variant="smallLabel"
          sx={{
            whiteSpace: 'normal',
            lineHeight: 1.2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
          color={theme.palette.Text.Secondary}
          textAlign="right"
        >
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: (params: GridRenderCellParams<DataSentrySystemsRowItem>) => (
        <Typography variant="body">{formatToK(params.value)}</Typography>
      ),
    },
  ]
  const isLoadingTableData = isLoading
  const displayDataGridEmptyState = !isLoadingTableData && !gridData && !debouncedSearchQuery
  const displayDataGrid = !isLoadingTableData && (gridData || (!gridData && debouncedSearchQuery))

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <SearchTextInput
          onChange={nextValue => {
            setSearchQuery(nextValue)
          }}
          value={searchQuery}
          showClearButton
          minWidth="176px"
          sx={{
            width: '176px',
            '& .MuiInputBase-input': { maxWidth: '112px', minWidth: '112px' },
          }}
        />

        <Box display="flex" alignItems="center" gap={1}>
          <TabSegmentedGroup
            size="small"
            value={systemsAuthorizationStatus}
            exclusive
            onChange={(_, value) => {
              setSystemsAuthorizationStatus(value)
              setPaginationModel(prev => ({ ...prev, page: 0 }))
            }}
          >
            <TabSegmented value={SystemsAuthorizationStatus.AUTHORIZED}>
              <Box display="flex" gap={0.5} alignItems="center">
                <Typography variant="label">Authorized</Typography>
              </Box>
            </TabSegmented>
            <TabSegmented value={SystemsAuthorizationStatus.UNAUTHORIZED}>
              <Box display="flex" gap={0.5} alignItems="center">
                <Typography variant="label">Unauthorized</Typography>
              </Box>
            </TabSegmented>
          </TabSegmentedGroup>
        </Box>
      </Box>

      {isLoadingTableData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} height={380}>
          <Spinner size="32px" thickness={2} />
        </Box>
      ) : null}

      {displayDataGridEmptyState ? (
        <Box display="flex" justifyContent="center" alignItems="center" height={380}>
          <EmptyState
            size="small"
            colorTheme={theme.palette.Text.Secondary}
            sx={{ backgroundColor: theme.palette.Black.o8, borderRadius: 4 }}
            customIcon={
              <Icon name={IconDictionary.OTableView} width="48px" height="48px" iconColor={theme.palette.Black.o48} />
            }
            title="No Data"
            subTitle="Review your settings and connections, or wait for the scan to finish processing."
          />
        </Box>
      ) : null}

      {displayDataGrid ? (
        <Box height={380}>
          <DataGrid
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnPinning
            disableColumnReorder
            disableColumnResize
            disableRowHoverStates
            disableBorder
            disableChildrenSorting={false}
            getRowHeight={() => 'auto'}
            getRowId={row => row.id}
            rowCount={rowCount}
            rows={gridData || []}
            columns={columns}
            pagination
            pageSizeOptions={[5, 10, 20]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            loading={isLoadingTableData}
            paginationMode="server"
            autosizeOnMount
            autosizeOptions={{
              expand: false,
              includeHeaders: true,
              includeOutliers: false,
            }}
            noRowsOverlayProps={{ buttonTitle: '' }}
          />
        </Box>
      ) : null}
    </Box>
  )
}
